import { ECatalogFilter } from "./ECatalogFilter";
import CatalogFilterOption from "./CatalogFilterOption";
import JQuery from "jquery";
import BaseMCatalogTemplate from "./BaseMCatalogTemplate";

/**
 * What a CatalogFilter needs from its owning filters object. `ApplyFilters` runs on every selection;
 * `FilterBy` only feeds the in-memory `UpdateCounter` path (Constellation), so it's optional — the
 * server-driven filters omit it and use SetCounts() instead.
 */
export interface CatalogFilterController {
  ApplyFilters(): void;
  FilterBy?(filter?: ECatalogFilter): BaseMCatalogTemplate[];
}

export default class CatalogFilter {
  private readonly type: ECatalogFilter;
  private controller: CatalogFilterController;
  private defaultOption: CatalogFilterOption;
  private readonly _optionsContainers: JQuery<HTMLElement>;
  private currentOption?: CatalogFilterOption = null;
  private readonly _options: Map<number, CatalogFilterOption>;

  constructor(controller: CatalogFilterController, type: ECatalogFilter) {
    this.type = type;
    this.controller = controller;
    this._options = new Map<number, CatalogFilterOption>();
    this._optionsContainers = $(`.filter-${type.valueOf()}`);

    this._optionsContainers.each((index, element) => {
      const { filter } = element.dataset;

      // Build the list of option for this filter
      // The region is a bit different from the other filters as it is a number[] instead of the usual number type
      if (this.type != ECatalogFilter.REGION || filter === "-1") {
        this._options.set(+filter, new CatalogFilterOption(+filter, element));
      } else {
        this._options.set(
          index,
          new CatalogFilterOption(JSON.parse(filter) as number[], element),
        );
      }
      if (index === 0) {
        const firstValue =
          this.type != ECatalogFilter.REGION || filter === "-1"
            ? +filter
            : index;
        this.defaultOption = this._options.get(firstValue);
        this.currentOption = this._options.get(firstValue);
      }
    });

    this._optionsContainers.on("click", (event) => {
      const { filter } = event.currentTarget.dataset;
      let selectedOption: CatalogFilterOption | null = null;
      if (this.type != ECatalogFilter.REGION || filter === "-1") {
        selectedOption = this.GetOptionByFilter(+filter);
      } else {
        selectedOption = this.GetOptionByFilter(JSON.parse(filter) as number[]);
      }
      this.SelectOption(selectedOption);
    });
  }

  /**
   * Return all option elements for this filter
   */
  public get options(): Map<number, CatalogFilterOption> {
    return this._options;
  }

  /**
   * Get the filter value for the current option selected
   */
  public get currentFilter(): number | null | number[] {
    return this.currentOption ? this.currentOption.filter : -1;
  }

  public get optionsContainers(): JQuery<HTMLElement> {
    return this._optionsContainers;
  }

  public SelectOption(selectedOption?: CatalogFilterOption): void {
    if (this.currentOption === selectedOption) {
      return;
    }
    this._optionsContainers.removeClass("filter-active");
    this.currentOption = selectedOption ? selectedOption : this.defaultOption;
    this.currentOption.SetActive();
    this.updateUrl();
    this.controller.ApplyFilters();
  }

  /**
   * Retrieve an option from its filter value
   * @param filter
   * @constructor
   */
  public GetOptionByFilter(
    filter: number | number[],
  ): CatalogFilterOption | null {
    let selectedOption: CatalogFilterOption | null = null;
    this._options.forEach((option) => {
      if (option.filter.toString() === filter.toString()) {
        selectedOption = option;
      }
    });
    return selectedOption;
  }

  /**
   * Update the counter for this filter options
   * @constructor
   */
  public UpdateCounter(): void {
    const filteredList = this.controller.FilterBy
      ? this.controller.FilterBy(this.type)
      : [];
    this.options.forEach((option) => {
      option.counter = filteredList.filter((p) =>
        p.HasFilter(option.filter, this.type),
      ).length;
    });
    if (this.currentOption) {
      this.currentOption.SetActive();
    }
  }

  /**
   * Set this filter's option counts from a server facet map (option id => count), instead of
   * recomputing them from the in-memory list. Used by the server-driven catalog.
   */
  public SetCounts(counts: Record<string, number>): void {
    if (!counts) {
      return;
    }
    this.options.forEach((option) => {
      option.counter = counts[option.filter.toString()] ?? 0;
    });
    if (this.currentOption) {
      this.currentOption.SetActive();
    }
  }

  public ApplyUrlFilter(): void {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const filter = urlParams.get(this.type.valueOf());
    if (!filter) {
      return;
    }
    let selectedOption: CatalogFilterOption | null = null;
    if (this.type != ECatalogFilter.REGION || filter === "-1") {
      selectedOption = this.GetOptionByFilter(+filter);
    } else {
      selectedOption = this.GetOptionByFilter(JSON.parse(filter) as number[]);
    }
    this.currentOption = selectedOption;
  }

  /**
   * Add/remove the current filter in URL
   * @private
   */
  private updateUrl(): void {
    const url = new URL(window.location.href);
    if (this.currentFilter === -1) {
      url.searchParams.delete(this.type.valueOf());
    } else {
      url.searchParams.set(this.type.valueOf(), this.currentFilter.toString());
    }
    window.history.pushState(
      { type: this.currentFilter.toString() },
      "",
      url.href,
    );
  }
}
